// src/react/fns/index.ts
var randomPlayingIndex = (index, length) => {
  const random = Math.floor(Math.random() * (length - index + 1)) + index;
  if (random === index) {
    return randomPlayingIndex(index, length);
  }
  return random;
};

// src/react/index.ts
import { useState, useRef, useCallback, useMemo } from "react";
import { useGain, useAudioContext } from "web-audio-hooks/react";
import { AudioProvider } from "web-audio-hooks/react";
var MODE = {
  Shuffle: `Shuffle`,
  SingleOnce: `SingleOnce`,
  SingleLoop: `SingleLoop`,
  SequentialOnce: `SequentialOnce`,
  SequentialLoop: `SequentialLoop`
};
var MODES = [`Shuffle`, `SingleOnce`, `SingleLoop`, `SequentialOnce`, `SequentialLoop`];
var useAudioList = (urls, {
  onEnded
} = {}) => {
  const context = useAudioContext();
  const audioRef = useRef(null);
  const audioPoolRef = useRef([]);
  const sourceRef = useRef(null);
  const gainNode = useGain(0.5);
  const [playMode, setPlayMode] = useState(MODE.SequentialOnce);
  const [playingIndex, setPlayingIndex] = useState(-1);
  const [audioUrls, setAudioList] = useState(urls);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const state = useMemo(() => ({
    audios: audioUrls,
    volume,
    playing,
    duration,
    currentTime,
    playbackRate,
    playMode,
    playingIndex
  }), [
    audioUrls,
    volume,
    playing,
    duration,
    currentTime,
    playbackRate,
    playMode,
    playingIndex
  ]);
  const getAudio = (url) => {
    const available = audioPoolRef.current.find((a) => a.ended);
    if (available && url) {
      available.src = url;
      return available;
    }
    return new Audio(url);
  };
  const initAudio = useCallback(
    (url) => {
      if (!context || !url || !gainNode) return;
      if (sourceRef.current) {
        audioRef.current?.pause();
        sourceRef.current.disconnect();
      }
      audioRef.current = getAudio(url);
      const audio = audioRef.current;
      audio.playbackRate = playbackRate;
      audio.crossOrigin = `anonymous`;
      sourceRef.current = context.createMediaElementSource(audio);
      sourceRef.current.connect(gainNode);
      gainNode.connect(context.destination);
      const updateTime = () => {
        setCurrentTime(audio.currentTime);
      };
      const updateMetadata = () => {
        setDuration(audio.duration);
      };
      const onAudioEnded = () => {
        onEnded?.();
        if (playMode === `SingleLoop`) {
          audio.currentTime = 0;
          audio.play();
        } else if (playMode === `SingleOnce`) {
          setPlaying(false);
          audioRef.current?.pause();
        } else {
          const nextIndex = getNextIndex(true);
          if (nextIndex !== -1) {
            playTrack(nextIndex);
          } else if (playMode === `SequentialOnce`) {
            setPlaying(false);
            audioRef.current?.pause();
          }
        }
        audioPoolRef.current.push(audio);
      };
      const onRateChange = () => {
        controls.setPlaybackRate(audio.playbackRate);
      };
      audio.addEventListener(`ratechange`, onRateChange);
      audio.addEventListener(`timeupdate`, updateTime);
      audio.addEventListener(`loadedmetadata`, updateMetadata);
      audio.addEventListener(`ended`, onAudioEnded);
      return () => {
        audio.pause();
        audioPoolRef.current.forEach((a) => a.pause());
        audioPoolRef.current = [];
        sourceRef.current?.disconnect();
        audio.removeEventListener(`ratechange`, onRateChange);
        audio.removeEventListener(`timeupdate`, updateTime);
        audio.removeEventListener(`loadedmetadata`, updateMetadata);
        audio.removeEventListener(`ended`, onAudioEnded);
      };
    },
    [context, gainNode, playbackRate, playMode]
  );
  const playTrack = useCallback(async (index) => {
    if (index < 0 || index >= audioUrls.length) {
      console.warn(`Invalid index: ${index}`);
      if (playMode === `SequentialOnce`) {
        setPlaying(false);
        audioRef.current?.pause();
      }
      return;
    }
    setPlayingIndex(index);
    setPlaying(false);
    initAudio(audioUrls[index]);
    try {
      if (context?.state === `suspended`) {
        await context.resume();
      }
      await audioRef.current?.play();
      setPlaying(true);
    } catch (err) {
      console.error(`\u64AD\u653E\u5931\u8D25:`, err);
    }
  }, [context, audioUrls, initAudio, playMode]);
  const getPrevIndex = () => {
    if (audioUrls.length === 0) return -1;
    if (playMode === `Shuffle`) {
      return randomPlayingIndex(playingIndex, audioUrls.length);
    }
    if (playingIndex <= 0) {
      return audioUrls.length - 1;
    }
    return playingIndex - 1;
  };
  const getNextIndex = (isFromEndedEvent = false) => {
    if (audioUrls.length === 0) return -1;
    if (audioUrls.length > 0 && playingIndex === -1) {
      return 0;
    }
    if (playMode === `Shuffle`) {
      return randomPlayingIndex(playingIndex, audioUrls.length);
    }
    if (playMode === `SingleOnce`) {
      return -1;
    }
    if (playingIndex >= audioUrls.length - 1) {
      if (playMode === `SequentialLoop`) {
        return 0;
      } else if (playMode === `SequentialOnce` && isFromEndedEvent) {
        return -1;
      }
    }
    return playingIndex + 1;
  };
  const playPrevTrack = () => {
    playTrack(getPrevIndex());
  };
  const playNextTrack = () => {
    playTrack(getNextIndex());
  };
  const controls = {
    play: async () => {
      if (audioRef.current) {
        await audioRef.current.play();
        setPlaying(true);
      } else {
        if (audioUrls.length > 0) {
          playTrack(0);
        }
      }
    },
    pause: () => {
      audioRef.current?.pause();
      setPlaying(false);
    },
    togglePlay: () => {
      playing ? controls.pause() : controls.play();
    },
    playTrack,
    setAudioList,
    prev: playPrevTrack,
    next: playNextTrack,
    seek: (time) => {
      if (audioRef.current) {
        audioRef.current.currentTime = time;
      }
    },
    fastForward: (ms = 5e3) => {
      if (audioRef.current) {
        const newTime = Math.min(audioRef.current.currentTime + ms / 1e3, audioRef.current.duration || 0);
        audioRef.current.currentTime = newTime;
      }
    },
    rewind: (ms = 5e3) => {
      if (audioRef.current) {
        const newTime = Math.max(audioRef.current.currentTime - ms / 1e3, 0);
        audioRef.current.currentTime = newTime;
      }
    },
    setVolume: (volume2) => {
      setVolume(volume2);
      if (gainNode) {
        gainNode.gain.setValueAtTime(volume2, context?.currentTime || 0);
      }
    },
    nextPlayMode: (_mode) => {
      const currentIndex = MODES.indexOf(playMode);
      const nextIndex = (currentIndex + 1) % MODES.length;
      const mode = _mode || MODES[nextIndex];
      setPlayMode(mode);
    },
    setPlaybackRate: (rate) => {
      rate = Math.min(Math.max(rate, 0.5), 3);
      setPlaybackRate(rate);
      if (audioRef.current) {
        audioRef.current.playbackRate = rate;
      }
    },
    switchAudio: setPlayingIndex
  };
  return {
    state,
    controls
  };
};
export {
  AudioProvider,
  MODE,
  useAudioList
};
